import {
  CurrencyDetails,
  DeductionsReportSettings,
  DineInSettings,
  MenuEditorSettings,
  NewMerchantVenueSettings,
  PriceFormatSetting,
} from '../../api/gen/DeliveryProviderAuthApi';
import { LocaleType } from '../../utils/translations_types';

export { AccountData } from '../../api/gen/DeliveryProviderAuthApi';

export interface VendorAssignedProvider {
  vendor_id: number;
  vendor_name: string;
  providers: ProviderPortalAssignedProvider[];
}

export interface ProviderPortalAssignedProvider {
  provider_id: number;
  provider_name: string;

  vendor_id: number;
  vendor_name: string;

  city_id: number;
  country_code: string;
  currency: CurrencyDetails;
  price_format_setting: PriceFormatSetting | null;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;
  campaigns_settings?: CampaignsSettings;

  /* deprecated */
  has_menu_editor?: boolean;
  menu_editor_settings?: MenuEditorSettings;
  dine_in_settings: DineInSettings;
  deductions_report_settings: DeductionsReportSettings;
  new_merchant_venue_settings: NewMerchantVenueSettings;
}

export interface ProviderPortalSelectedProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;

  cityId: number;
  countryCode: string;
  currency: CurrencyDetails;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;

  menuEditorSettings: MenuEditorSettings;
  campaigns_settings: CampaignsSettings;

  permissions: AccountPermissions;
}

export interface SelectedVendorAndProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;
}

export interface CampaignsSettings {
  can_self_assign_campaign: boolean;
  can_manage_promotions?: boolean;
  is_menu_inflation?: boolean;
  is_metrics_satisfied?: boolean;
  can_setup_bolt_plus_promotions?: boolean;
  can_manage_smart_promotions?: boolean;
}

export interface AccountPermissions {
  menuEditorAccess: boolean;
  selfAssignCampaigns: boolean;
  canSeePromotions: boolean;
  canSeeSmartPromotions: boolean;
  canCreateFoodPromotions: boolean;
  canCreatePromotions: boolean;
  canSetupBoltPlusPromotions: boolean;
  dineInAccess: boolean;
  deductionsAccess: boolean;
  newMerchantVenueAccess: boolean;
}

export enum AccountStage {
  ONBOARDING = 'onboarding',
  ACTIVATED = 'activated',
}

export function mapAssignedProviderToSelectedProvider(
  provider: ProviderPortalAssignedProvider,
): ProviderPortalSelectedProvider {
  return {
    providerId: provider.provider_id,
    providerName: provider.provider_name,

    vendorId: provider.vendor_id,
    vendorName: provider.vendor_name,

    cityId: provider.city_id,
    countryCode: provider.country_code,
    currency: provider.currency,

    city_locales:
      provider.city_locales && provider.city_locales.length
        ? provider.city_locales
        : [LocaleType.English],
    city_main_locale: provider.city_main_locale ?? LocaleType.English,

    menuEditorSettings: provider.menu_editor_settings,
    campaigns_settings: provider.campaigns_settings,

    permissions: {
      menuEditorAccess: Boolean(
        provider.has_menu_editor || provider.menu_editor_settings?.is_enabled || false,
      ),
      selfAssignCampaigns: Boolean(provider.campaigns_settings?.can_self_assign_campaign),
      canSeePromotions: Boolean(
        provider.campaigns_settings?.can_self_assign_campaign ||
          provider.campaigns_settings?.can_manage_promotions,
      ),
      canSeeSmartPromotions: Boolean(provider.campaigns_settings?.can_manage_smart_promotions),
      canCreateFoodPromotions: !provider.campaigns_settings?.is_menu_inflation,
      canCreatePromotions: Boolean(provider.campaigns_settings?.is_metrics_satisfied),
      canSetupBoltPlusPromotions: Boolean(
        provider.campaigns_settings?.can_setup_bolt_plus_promotions,
      ),
      dineInAccess: Boolean(
        provider.dine_in_settings.is_order_and_pay_enabled ||
          provider.dine_in_settings.is_pay_and_go_enabled,
      ),
      deductionsAccess: provider.deductions_report_settings.is_deductions_report_enabled,
      newMerchantVenueAccess: provider.new_merchant_venue_settings?.is_enabled,
    },
  };
}

export function mapSelectedProviderDataToSelectedVendorAndProvider(
  provider: ProviderPortalSelectedProvider,
): SelectedVendorAndProvider {
  return {
    providerId: provider.providerId,
    providerName: provider.providerName,
    vendorId: provider.vendorId,
    vendorName: provider.vendorName,
  };
}

export function getSelectedProviderData(
  selectedProvider: number,
  assignedProviders: ProviderPortalAssignedProvider[],
): ProviderPortalSelectedProvider {
  if (!assignedProviders?.length) {
    return null;
  }

  const firstProvider = assignedProviders[0];

  // if we do not have selected provider, we return first 1 as selected
  if (!selectedProvider) {
    return mapAssignedProviderToSelectedProvider(firstProvider);
  }

  // we search for selected provider
  const providerData = assignedProviders?.find((item) => item.provider_id === selectedProvider);

  // if we do not have selected provider, we return first 1 as selected
  return mapAssignedProviderToSelectedProvider(providerData || firstProvider);
}

export type SelectedProvider = ReturnType<typeof mapAssignedProviderToSelectedProvider>;
export type ProviderPermissions = ReturnType<
  typeof mapAssignedProviderToSelectedProvider
>['permissions'];
